import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Boxes, Wrench, Cog, ClipboardList, Factory, FileText,
  Activity, Users, ShieldCheck, Bell, Calendar as CalIcon, Mail, Tag,
  Package, MessageCircle, Cpu, FileBarChart, Truck, ListChecks, ScanLine,
  Settings, Receipt, Building2, ShoppingCart, AlertTriangle, BarChart3,
  CalendarDays, FilePlus2, Clock, Briefcase, FlaskConical
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Define tiles inspired by Windows 8 Metro UI
// Sizes: 1x1 (small), 2x1 (wide), 2x2 (big)
const TILE_DEFS = [
  // Linha 1 - Operacao Diaria
  { id: "shop-floor", to: "/shop-floor", title: "Chão de Fábrica", icon: Factory, color: "#0078d4", size: "2x2", roles: ["admin","operator","quality","shift_leader","display"], showStat: "running_orders" },
  { id: "operator", to: "/operator-panel", title: "Painel Operador", icon: Cpu, color: "#107c10", size: "2x1", roles: ["admin","operator","quality","shift_leader"] },
  { id: "orders", to: "/orders", title: "Ordens", icon: ClipboardList, color: "#5c2d91", size: "1x1", roles: ["admin","quality","shift_leader"], showStat: "orders_count" },
  { id: "planeamento", to: "/planeamento", title: "Planeamento", icon: CalIcon, color: "#008272", size: "1x1", roles: ["admin","quality","shift_leader"] },

  // Linha 2 - Producao + Qualidade
  { id: "oee", to: "/oee", title: "Dashboard OEE", icon: Activity, color: "#1ba1e2", size: "2x2", roles: ["admin","quality","shift_leader"], showStat: "oee_global" },
  { id: "quality", to: "/quality", title: "Qualidade", icon: ShieldCheck, color: "#d83b01", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "iso9001", to: "/iso9001", title: "ISO 9001", icon: ListChecks, color: "#a4262c", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "quality-inspections", to: "/quality-inspections", title: "Inspeções", icon: FlaskConical, color: "#ca5010", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "alerts", to: "/alerts", title: "Alertas", icon: AlertTriangle, color: "#c50f1f", size: "1x1", roles: ["admin","quality","shift_leader"], showStat: "active_alerts" },

  // Linha 3 - Manutencao + Maquinas
  { id: "preventive-maintenance", to: "/preventive-maintenance", title: "Manutenção Preventiva", icon: Wrench, color: "#ca5010", size: "2x1", roles: ["admin","quality","shift_leader"], showStat: "pm_overdue" },
  { id: "machine-management", to: "/machine-management", title: "Gestão Máquinas", icon: Cpu, color: "#7a7574", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "machines", to: "/machines", title: "Máquinas", icon: Cog, color: "#737373", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "molds", to: "/molds", title: "Moldes", icon: Boxes, color: "#525252", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "parts", to: "/parts", title: "Peças", icon: Package, color: "#3a3a3a", size: "1x1", roles: ["admin","quality","shift_leader"] },

  // Linha 4 - Comercial / Vendas
  { id: "clients", to: "/clients", title: "Clientes", icon: Building2, color: "#b4009e", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "sales-guides", to: "/sales-guides", title: "Guias de Venda", icon: Receipt, color: "#e3008c", size: "2x1", roles: ["admin","quality","shift_leader"] },
  { id: "billing", to: "/billing", title: "Faturação", icon: FileText, color: "#5c2e91", size: "1x1", roles: ["admin"] },
  { id: "sales-report", to: "/sales-report", title: "Relatório Vendas", icon: BarChart3, color: "#681bc4", size: "1x1", roles: ["admin","quality","shift_leader"] },

  // Linha 5 - Stock + Materiais
  { id: "stocks", to: "/stocks", title: "Stock", icon: Boxes, color: "#0099bc", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "raw-materials", to: "/raw-materials", title: "Matéria-Prima", icon: FlaskConical, color: "#00b294", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "purchase-orders", to: "/purchase-orders", title: "Notas Encomenda", icon: ShoppingCart, color: "#018574", size: "2x1", roles: ["admin"] },
  { id: "packaging", to: "/packaging", title: "Embalagem", icon: Package, color: "#005a9e", size: "1x1", roles: ["admin","quality","shift_leader"] },

  // Linha 6 - Documentacao + Etiquetas
  { id: "work-instructions", to: "/work-instructions", title: "Instruções Trabalho", icon: FilePlus2, color: "#8764b8", size: "2x1", roles: ["admin","quality","shift_leader"] },
  { id: "part-labels", to: "/part-labels", title: "Etiquetas QR", icon: ScanLine, color: "#744da9", size: "1x1", roles: ["admin","quality","shift_leader","operator"] },
  { id: "reports", to: "/reports", title: "Relatórios", icon: FileBarChart, color: "#5c2e91", size: "1x1", roles: ["admin","quality","shift_leader"] },

  // Linha 7 - Pessoas + Gestao
  { id: "users", to: "/users", title: "Utilizadores", icon: Users, color: "#0f6cbd", size: "1x1", roles: ["admin"] },
  { id: "shift-schedule", to: "/shift-schedule", title: "Turnos", icon: Clock, color: "#0078d4", size: "1x1", roles: ["admin","operator","quality","shift_leader"] },
  { id: "messages", to: "/messages", title: "Mensagens", icon: MessageCircle, color: "#038387", size: "1x1", roles: ["admin","operator","quality","shift_leader"], showStat: "unread_messages" },
  { id: "cost-control", to: "/cost-control", title: "Controlo Custos", icon: Briefcase, color: "#7a7574", size: "1x1", roles: ["admin"] },

  // Linha 8 - Configuracoes
  { id: "notification-settings", to: "/notification-settings", title: "Notificações", icon: Bell, color: "#a80000", size: "1x1", roles: ["admin"] },
  { id: "settings", to: "/settings", title: "Definições", icon: Settings, color: "#5d5a58", size: "1x1", roles: ["admin","quality","shift_leader"] },
  { id: "dashboard", to: "/dashboard", title: "Painel Geral", icon: LayoutDashboard, color: "#243a5e", size: "1x1", roles: ["admin","operator","quality","shift_leader"] },
];

const sizeClass = (size) => {
  switch (size) {
    case "2x2": return "col-span-2 row-span-2 h-[280px]";
    case "2x1": return "col-span-2 row-span-1 h-[136px]";
    case "1x2": return "col-span-1 row-span-2 h-[280px]";
    default: return "col-span-1 row-span-1 h-[136px]";
  }
};

const Tile = ({ t, stats }) => {
  const Icon = t.icon;
  const stat = t.showStat ? stats[t.showStat] : null;
  const isBig = t.size === "2x2";
  const isWide = t.size === "2x1";
  
  return (
    <Link
      to={t.to}
      className={`${sizeClass(t.size)} group relative overflow-hidden text-white shadow-md hover:shadow-2xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]`}
      style={{ backgroundColor: t.color }}
      data-testid={`tile-${t.id}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute inset-0 p-4 flex flex-col justify-between">
        <Icon className={`${isBig ? "h-12 w-12" : isWide ? "h-9 w-9" : "h-8 w-8"} drop-shadow-lg`} strokeWidth={1.5} />
        <div className="space-y-1">
          {stat != null && (
            <div className={`${isBig ? "text-5xl" : "text-3xl"} font-bold drop-shadow-md tracking-tight leading-none`}>
              {stat.value}
              {stat.unit && <span className={`${isBig ? "text-2xl" : "text-lg"} ml-1 opacity-80`}>{stat.unit}</span>}
            </div>
          )}
          {stat?.label && <div className="text-xs opacity-80">{stat.label}</div>}
          <div className={`${isBig ? "text-lg" : isWide ? "text-base" : "text-sm"} font-semibold leading-tight`}>{t.title}</div>
        </div>
      </div>
      {/* tile shine */}
      <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-bl from-white/10 to-transparent rotate-12 pointer-events-none" />
    </Link>
  );
};

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const promises = [
          axios.get(`${API}/orders`).catch(() => ({ data: [] })),
          axios.get(`${API}/oee/dashboard`).catch(() => ({ data: { global: { oee: 0 } } })),
          axios.get(`${API}/preventive-maintenance/plans`).catch(() => ({ data: [] })),
          axios.get(`${API}/alerts`).catch(() => ({ data: [] })),
        ];
        const [ordersRes, oeeRes, pmRes, alertsRes] = await Promise.all(promises);
        if (!mounted) return;
        const orders = ordersRes.data || [];
        const running = orders.filter(o => o.status === "running").length;
        const pending = orders.filter(o => o.status === "pending").length;
        const oeeVal = Math.round(oeeRes.data?.global?.oee || 0);
        const overdue = (pmRes.data || []).filter(p => p.status === "overdue").length;
        const dueSoon = (pmRes.data || []).filter(p => p.status === "due_soon").length;
        const activeAlerts = (alertsRes.data || []).filter(a => !a.resolved).length;

        setStats({
          orders_count: { value: orders.length, label: `${running} a correr / ${pending} pendentes` },
          running_orders: { value: running, label: `${pending} pendentes`, unit: "" },
          oee_global: { value: oeeVal, unit: "%", label: oeeVal >= 85 ? "Classe Mundial" : oeeVal >= 60 ? "Aceitável" : "Crítico" },
          pm_overdue: overdue > 0 ? { value: overdue, label: `${dueSoon} a vencer` } : { value: dueSoon, label: "a vencer" },
          active_alerts: activeAlerts > 0 ? { value: activeAlerts, label: "Activos" } : null,
          unread_messages: null,
        });
      } catch (e) { /* ignore */ }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const visibleTiles = TILE_DEFS.filter(t => !t.roles || t.roles.includes(user?.role));
  const now = new Date();
  const greeting = (() => {
    const h = now.getHours();
    if (h < 12) return "Bom dia";
    if (h < 19) return "Boa tarde";
    return "Boa noite";
  })();

  return (
    <div className="min-h-screen bg-slate-900 p-8" data-testid="home-tiles">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-slate-400 text-sm">{now.toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          <h1 className="text-4xl text-white font-light tracking-tight mt-1">
            {greeting}, <span className="font-semibold">{user?.name || user?.user_code}</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">EP3D Engineering Project · Sistema MES</p>
        </div>
        <div className="text-right">
          <div className="text-5xl text-white font-extralight tracking-tight">{now.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</div>
        </div>
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 auto-rows-[136px]">
        {visibleTiles.map(t => (
          <Tile key={t.id} t={t} stats={stats} />
        ))}
      </div>

      <div className="mt-10 text-center text-xs text-slate-500">
        EP3D Engineering Project · NIF: 517 316 188 · {now.getFullYear()}
      </div>
    </div>
  );
}
